import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "context/AuthContext";
import { Button } from "./ui/Button";
import { Modal } from "./ui/Modal";
import { TextEdit } from "./ui/TextEdit";
import fetchWithCredentials from "apiClient";
import { Search, User, Mail, School } from "lucide-react";
import { Manager } from "types/Manager";

interface ImpersonationDialogProps {
  open: boolean;
  onClose: () => void;
}

export function ImpersonationDialog({
  open,
  onClose,
}: ImpersonationDialogProps) {
  const { startImpersonation, lang } = useAuth();
  const locale = lang === "es" ? "es" : "en";
  const [managers, setManagers] = useState<Manager[]>([]);
  const [filteredManagers, setFilteredManagers] = useState<Manager[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchManagers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchWithCredentials("/manager-api/all-managers/");
      setManagers(data);
    } catch (err: any) {
      setError(
        err.message ||
          (locale === "es"
            ? "No se pudieron cargar los administradores"
            : "Failed to fetch managers")
      );
    } finally {
      setLoading(false);
    }
  }, [locale]);

  useEffect(() => {
    if (open) {
      setSearchTerm("");
      fetchManagers();
    }
  }, [open, fetchManagers]);

  useEffect(() => {
    const term = (searchTerm ?? "").trim().toLowerCase();
    const filtered = term
      ? managers.filter(
          (manager) =>
            (manager.contact?.name ?? "")
              .toLowerCase()
              .includes(term) ||
            (manager.contact?.mail ?? "").toLowerCase().includes(term) ||
            (manager.propertyManagerName ?? "")
              .toLowerCase()
              .includes(term)
        )
      : managers;
    setFilteredManagers(filtered);
  }, [managers, searchTerm]);

  const handleImpersonate = async (userId: number) => {
    try {
      await startImpersonation(userId);
      onClose();
    } catch (err: any) {
      setError(
        err.message ||
          (locale === "es"
            ? "No se pudo iniciar la suplantación"
            : "Failed to start impersonation")
      );
    }
  };

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title={locale === "es" ? "Impersonar administrador" : "Impersonate Manager"}
    >
      <div className="max-w-2xl w-full">
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 z-10" />
            <TextEdit
              id="manager-search"
              name="manager-search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              label=""
              placeholder={
                locale === "es"
                  ? "Buscar administradores por nombre o por el nombre de la administración..."
                  : "Search property managers by name or property manager name..."
              }
              className="pl-10"
            />
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="max-h-96 overflow-y-auto space-y-2">
            {filteredManagers.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                {searchTerm
                  ? locale === "es"
                    ? "No se encontraron administradores con tu búsqueda."
                    : "No managers found matching your search."
                  : locale === "es"
                    ? "No hay administradores disponibles."
                    : "No managers available."}
              </div>
            ) : (
              filteredManagers.map((manager) => (
                <div
                  key={manager.userId}
                  className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <User className="w-4 h-4 text-gray-400" />
                        <span className="font-medium text-gray-900">
                          {manager.contact.name}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2 mb-1">
                        <Mail className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-600">
                          {manager.contact.mail}
                        </span>
                      </div>
                      {manager.propertyManagerName && (
                        <div className="flex items-center space-x-2">
                          <School className="w-4 h-4 text-gray-400" />
                          <span className="text-sm text-gray-600">
                            {manager.propertyManagerName}
                          </span>
                        </div>
                      )}
                    </div>
                    <Button
                      onClick={() => handleImpersonate(manager.userId)}
                      size="sm"
                      className="ml-4"
                    >
                      {locale === "es" ? "Impersonar" : "Impersonate"}
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
